/* ==========================================================================
   LONDON REPAIR OUTLET - DEVICE FINDER
   File: assets/lro-finder.js
   Enhances: sections/lro-device-finder.liquid
   Styles:   assets/lro-finder.css
   Data:     assets/lro-finder-index.json  (scripts/build_finder_index.py)
             assets/lro-nav.json           (fallback, scripts/build_nav_index.py)

   WHAT THIS FILE IS FOR
   ---------------------
   Make -> model -> part -> results, in three taps, over 14,560 products and
   7,829 device models. Everything here exists to keep those three taps fast:

     - Nothing is fetched on page load. The index is asked for on the first
       interaction with the section, or when the section comes within 400px of
       the viewport, whichever happens first.
     - The index is ~205 KB of front-coded JSON and is parsed once per page,
       then kept in sessionStorage under a key that carries the asset version,
       so the second page view costs no network and no revalidation.
     - Step one needs none of it. The thirteen makes are real markup rendered
       by Liquid, so the first tap is instant even on a cold cache.
     - Step two is a typeahead, not a <select>. Samsung alone has 2,789 models.

   PROGRESSIVE ENHANCEMENT
   -----------------------
   The section ships a working three-<select> form. This file hides it and
   reveals the panel. It does not remove the form, and it writes every choice
   back into the form's fields, so if anything here throws after start-up the
   submit button still performs a sensible search.

   No dependencies, no build step, no globals. If this file fails to parse,
   the form is what is left, and the form works.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Small helpers
   -------------------------------------------------------------------------- */

/** The rule Shopify uses to turn a tag into a URL segment. Kept identical to
 *  js_handleize() in scripts/build_finder_index.py; the index carries an
 *  explicit handle for the rare name where the two would disagree. */
function handleize(value) {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Lower case, punctuation flattened to single spaces. Used for searching. */
function normalise(value) {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/^ +| +$/g, '');
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

let formatter = null;
function formatCount(n) {
  if (formatter === null) {
    try {
      formatter = new Intl.NumberFormat('en-GB');
    } catch (error) {
      formatter = { format: function (value) { return String(value); } };
    }
  }
  return formatter.format(n);
}

/** "{count} parts for {model}" -> the real thing. */
function interpolate(template, values) {
  return String(template || '').replace(/\{(\w+)\}|\[(\w+)\]/g, function (whole, braceKey, bracketKey) {
    var key = braceKey || bracketKey;
    return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole;
  });
}

/* --------------------------------------------------------------------------
   The index
   -------------------------------------------------------------------------- */

/** url -> Promise, so two finders on one page share one fetch. */
const inFlight = new Map();

function readCache(key) {
  try {
    const cached = window.sessionStorage.getItem(key);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    return null;
  }
}

function writeCache(key, text) {
  try {
    window.sessionStorage.setItem(key, text);
  } catch (error) {
    /* Private mode, or the quota is full. The HTTP cache still has our back. */
  }
}

function fetchJson(url) {
  return fetch(url, { credentials: 'same-origin' }).then(function (response) {
    if (!response.ok) throw new Error(url + ' responded ' + response.status);
    return response.text();
  });
}

/** Normalise the compact index into the shape the UI wants. Model lists stay
 *  encoded until the make they belong to is actually chosen. */
function adaptIndex(raw) {
  return {
    partial: false,
    sep: raw.sep || '~',
    groups: raw.groups || [],
    cats: (raw.cats || []).map(function (row) {
      return { name: row[0], handle: row[1], count: row[2], group: row[3] };
    }),
    brands: (raw.brands || []).map(function (brand) {
      return {
        name: brand.n,
        handle: brand.h,
        tag: brand.t || brand.n,
        count: brand.c,
        encodedCats: brand.k || '',
        encodedModels: brand.m || [],
        cats: null,
        models: null
      };
    })
  };
}

/** The lifeboat: assets/lro-nav.json can drive a reduced finder if the
 *  purpose-built index has not been generated yet. Models are there, but the
 *  part categories behind each model are not, so step three offers the whole
 *  taxonomy rather than only what that model has. */
function adaptNav(raw) {
  const cats = (raw.types || []).map(function (type, i) {
    return { name: type.name, handle: type.handle, count: type.count, group: 0, order: i };
  });
  return {
    partial: true,
    sep: '~',
    groups: ['All parts'],
    cats: cats,
    brands: (raw.brands || []).map(function (brand) {
      const rows = (raw.models_by_brand || {})[brand.name] || [];
      return {
        name: brand.name,
        handle: brand.handle,
        tag: brand.tag || brand.name,
        count: brand.count,
        encodedCats: '',
        encodedModels: [],
        cats: null,
        models: rows.map(function (model) {
          return {
            name: model.name,
            handle: handleize(model.name),
            count: model.count,
            cats: null,
            norm: normalise(model.name)
          };
        }).sort(function (a, b) { return a.name.localeCompare(b.name); })
      };
    })
  };
}

function loadIndex(indexUrl, navUrl) {
  if (inFlight.has(indexUrl)) return inFlight.get(indexUrl);

  const cacheKey = 'lro-finder:' + indexUrl;
  const promise = new Promise(function (resolve, reject) {
    const cached = readCache(cacheKey);
    if (cached) {
      resolve(cached.v ? adaptIndex(cached) : adaptNav(cached));
      return;
    }
    fetchJson(indexUrl)
      .then(function (text) {
        writeCache(cacheKey, text);
        resolve(adaptIndex(JSON.parse(text)));
      })
      .catch(function () {
        if (!navUrl) { reject(new Error('no index')); return; }
        fetchJson(navUrl)
          .then(function (text) {
            writeCache(cacheKey, text);
            resolve(adaptNav(JSON.parse(text)));
          })
          .catch(reject);
      });
  });

  inFlight.set(indexUrl, promise);
  return promise;
}

/* --------------------------------------------------------------------------
   Decoding
   Mirrors the encoder documented at the top of scripts/build_finder_index.py.
   -------------------------------------------------------------------------- */

/** "3:j,7:2,c" -> [{ cat, count }], plus everything the residual adds. */
function decodeCounts(field, cats) {
  const out = { list: [], total: 0 };
  if (!field) return out;
  const tokens = field.split(',');
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (!token) continue;
    const colon = token.indexOf(':');
    const key = colon === -1 ? token : token.slice(0, colon);
    const count = colon === -1 ? 1 : parseInt(token.slice(colon + 1), 36);
    if (!isFinite(count)) continue;
    out.total += count;
    if (key === '*') continue;               /* counted, but not filterable */
    const cat = cats[parseInt(key, 36)];
    if (cat) out.list.push({ cat: cat, count: count });
  }
  return out;
}

/** Front-coded model records -> objects. Called once per make, on first use. */
function decodeModels(brand, index) {
  if (brand.models) return brand.models;
  const sep = index.sep;
  const cats = index.cats;
  const records = brand.encodedModels;
  const models = [];
  let previous = '';

  for (let i = 0; i < records.length; i += 1) {
    const record = records[i];
    const shared = parseInt(record.charAt(0), 36);
    if (!isFinite(shared)) continue;
    const rest = record.slice(1);
    const cut = rest.indexOf(sep);
    if (cut === -1) continue;
    const name = previous.slice(0, shared) + rest.slice(0, cut);
    previous = name;

    const tail = rest.slice(cut + 1).split(sep);
    const counts = decodeCounts(tail[0], cats);
    models.push({
      name: name,
      handle: tail.length > 1 && tail[1] ? tail[1] : handleize(name),
      count: counts.total,
      cats: counts.list,
      norm: normalise(name)
    });
  }

  brand.models = models;
  return models;
}

function decodeBrandCats(brand, index) {
  if (brand.cats) return brand.cats;
  brand.cats = decodeCounts(brand.encodedCats, index.cats).list;
  return brand.cats;
}

/* --------------------------------------------------------------------------
   Searching the model list
   -------------------------------------------------------------------------- */

/* Ranking weights. Named so they can be argued with. */
const LENGTH_COST = 4;   /* points per character of model name                */
const DEPTH_WORTH = 6;   /* points per part behind the model                  */
const DEPTH_CAP = 60;    /* beyond which more parts stop being more convincing */

/** Lower is better. null means "no match at all".
 *
 *  Three signals, weighted so no single one can run away with the result:
 *
 *    match quality  exact, then prefix, then every word of the query landing
 *                   on a word boundary, then anywhere at all
 *    brevity        the catalogue contains model names that are really whole
 *                   product titles - "Note 8 Replacement Rear Battery Cover
 *                   with Camera Lens". They match plenty and are almost never
 *                   what was meant, so length costs 4 points a character
 *    depth          a model with thirty parts behind it is more likely to be
 *                   the device somebody is holding than one with a single
 *                   part, worth 4 points each up to fifty
 *
 *  Tuned against real queries: "s21" puts Galaxy S21 above "S21 Plus 5G G996
 *  Replacement Battery 4800mAh", which a pure prefix rule does not. */
function score(model, query, tokens) {
  const name = model.norm;
  let base;

  if (name === query) {
    base = -400;
  } else if (name.indexOf(query) === 0) {
    base = -200;
  } else {
    base = 0;
    let allOnWordBoundaries = true;
    for (let i = 0; i < tokens.length; i += 1) {
      const token = tokens[i];
      const atWord = (' ' + name).indexOf(' ' + token);
      if (atWord !== -1) {
        base += atWord;
      } else {
        const anywhere = name.indexOf(token);
        if (anywhere === -1) return null;
        allOnWordBoundaries = false;
        base += 300 + anywhere;
      }
    }
    if (allOnWordBoundaries) base -= 100;
  }

  return base + name.length * LENGTH_COST - Math.min(model.count, DEPTH_CAP) * DEPTH_WORTH;
}

function searchModels(brand, rawQuery, limit) {
  const models = brand.models || [];
  const query = normalise(rawQuery);

  /* No query yet: the most-stocked models, which is what somebody browsing
     rather than searching wants to see. Sorted once per make, not per key. */
  if (!query) {
    if (!brand.byCount) {
      brand.byCount = models.slice().sort(function (a, b) {
        return b.count - a.count || a.name.localeCompare(b.name);
      });
    }
    return { hits: brand.byCount.slice(0, limit), total: brand.byCount.length, query: query };
  }

  const tokens = query.split(' ');
  const scored = [];
  for (let i = 0; i < models.length; i += 1) {
    const value = score(models[i], query, tokens);
    if (value !== null) scored.push({ model: models[i], score: value });
  }

  /* Nothing matched with spaces where the shopper put them. Try again with all
     punctuation removed on both sides, so "iphone13promax" finds
     "iPhone 13 Pro Max". */
  if (!scored.length) {
    const flat = query.replace(/ /g, '');
    for (let i = 0; i < models.length; i += 1) {
      const position = models[i].norm.replace(/ /g, '').indexOf(flat);
      if (position !== -1) {
        scored.push({ model: models[i], score: position + models[i].name.length });
      }
    }
  }

  scored.sort(function (a, b) {
    return a.score - b.score || a.model.name.localeCompare(b.model.name);
  });
  return {
    hits: scored.slice(0, limit).map(function (entry) { return entry.model; }),
    total: scored.length,
    query: query
  };
}

/** Wrap every matched run in <mark>.
 *  The split happens on the raw name and each piece is escaped afterwards, so
 *  a search for "amp" cannot land inside the "&amp;" of an escaped ampersand
 *  and break the entity. */
function highlight(name, query) {
  if (!query) return escapeHtml(name);
  const tokens = query.split(' ').filter(Boolean).map(escapeRegExp);
  if (!tokens.length) return escapeHtml(name);

  let pattern;
  try {
    pattern = new RegExp('(' + tokens.join('|') + ')', 'gi');
  } catch (error) {
    return escapeHtml(name);
  }

  let out = '';
  let last = 0;
  let match = pattern.exec(name);
  while (match !== null) {
    if (match[0].length === 0) { pattern.lastIndex += 1; match = pattern.exec(name); continue; }
    out += escapeHtml(name.slice(last, match.index)) +
      '<mark class="lro-finder__mark">' + escapeHtml(match[0]) + '</mark>';
    last = match.index + match[0].length;
    match = pattern.exec(name);
  }
  return out + escapeHtml(name.slice(last));
}

/* --------------------------------------------------------------------------
   The finder
   -------------------------------------------------------------------------- */

class DeviceFinder {
  constructor(root) {
    this.root = root;
    this.uid = root.id || ('lro-finder-' + Math.random().toString(36).slice(2, 8));
    this.anchor = root.getAttribute('data-anchor') || this.uid;
    this.indexUrl = root.getAttribute('data-index-url');
    this.navUrl = root.getAttribute('data-nav-url');
    this.collectionsUrl = (root.getAttribute('data-collections-url') || '/collections')
      .replace(/\/+$/, '');
    this.showCounts = root.getAttribute('data-show-counts') !== 'false';
    this.maxOptions = parseInt(root.getAttribute('data-max-options'), 10) || 60;

    this.form = root.querySelector('[data-lro-form]');
    this.basic = root.querySelector('[data-lro-basic]');
    this.panel = root.querySelector('[data-lro-panel]');
    if (!this.form || !this.basic || !this.panel) return;

    this.basicMake = root.querySelector('[data-lro-basic-make]');
    this.basicModel = root.querySelector('[data-lro-basic-model]');
    this.basicPart = root.querySelector('[data-lro-basic-part]');

    this.makesBox = root.querySelector('[data-lro-makes]');
    this.partsBox = root.querySelector('[data-lro-parts]');
    this.partHelp = root.querySelector('[data-lro-part-help]');
    this.modelHelp = root.querySelector('[data-lro-model-help]');
    this.search = root.querySelector('[data-lro-search]');
    this.listbox = root.querySelector('[data-lro-listbox]');
    this.clearButton = root.querySelector('[data-lro-clear-model]');
    this.trail = root.querySelector('[data-lro-trail]');
    this.status = root.querySelector('[data-lro-status]');
    this.goButton = root.querySelector('[data-lro-go]');
    this.resetButton = root.querySelector('[data-lro-reset]');
    this.steps = {};
    const stepNodes = root.querySelectorAll('[data-lro-step]');
    for (let i = 0; i < stepNodes.length; i += 1) {
      this.steps[stepNodes[i].getAttribute('data-lro-step')] = stepNodes[i];
    }

    this.strings = this.readStrings();
    this.index = null;
    this.loading = false;
    this.pending = null;
    this.failed = false;
    this.state = { make: null, model: null, part: null };
    this.hits = [];
    this.activeIndex = -1;
    this.pendingHash = this.readHash();

    this.enhance();
  }

  readStrings() {
    const node = this.root.querySelector('[data-lro-finder-strings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  /* ---- start-up ------------------------------------------------------- */

  enhance() {
    this.root.classList.add('lro-finder--enhanced');
    this.basic.hidden = true;
    /* The panel now guards the choice, so the browser must not refuse to
       submit a form whose hidden select is empty. */
    if (this.basicModel) this.basicModel.removeAttribute('required');
    this.panel.hidden = false;

    this.bind();
    this.renderTrail();
    this.renderStatus();

    if (this.pendingHash) {
      this.warm();
    } else {
      this.watch();
    }
  }

  /** Ask for the index when the section is nearly on screen, or the moment
   *  anybody touches it. Never on page load. */
  watch() {
    const warm = this.warm.bind(this);
    const options = { once: true };
    this.root.addEventListener('pointerdown', warm, options);
    this.root.addEventListener('focusin', warm, options);
    this.root.addEventListener('keydown', warm, options);

    if (typeof IntersectionObserver !== 'function') return;
    const observer = new IntersectionObserver(function (entries) {
      for (let i = 0; i < entries.length; i += 1) {
        if (entries[i].isIntersecting) {
          observer.disconnect();
          /* Coming into view is a prediction, not a request. Wait for a quiet
             moment so the index never competes with the rest of the page. */
          if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(warm, { timeout: 2000 });
          } else {
            window.setTimeout(warm, 200);
          }
          return;
        }
      }
    }, { rootMargin: '400px 0px' });
    observer.observe(this.root);
  }

  /** Always hands back the same promise while a load is in flight. Returning a
   *  resolved null instead would silently drop a tap that landed mid-fetch,
   *  which is exactly when somebody is most likely to tap. */
  warm() {
    if (this.index) return Promise.resolve(this.index);
    if (this.failed) return Promise.resolve(null);
    if (this.pending) return this.pending;
    if (!this.indexUrl) { this.failed = true; return Promise.resolve(null); }
    this.loading = true;
    this.root.setAttribute('data-loading', 'true');
    /* Said quietly, in the field that is waiting on it, rather than announced
       into the status region where it would interrupt. */
    if (this.search && this.search.disabled && this.strings.loading) {
      this.search.placeholder = this.strings.loading;
    }

    const self = this;
    const restore = function () {
      if (self.search && self.search.disabled) {
        self.search.placeholder = self.strings.modelPlaceholder || '';
      }
    };

    this.pending = loadIndex(this.indexUrl, this.navUrl)
      .then(function (index) {
        self.index = index;
        self.loading = false;
        self.pending = null;
        self.root.removeAttribute('data-loading');
        restore();
        self.applyHash();
        return index;
      })
      .catch(function () {
        self.loading = false;
        self.pending = null;
        self.failed = true;
        self.root.removeAttribute('data-loading');
        restore();
        self.root.setAttribute('data-failed', 'true');
        self.say(self.strings.errorLoading || '');
        return null;
      });
    return this.pending;
  }

  bind() {
    const self = this;

    if (this.makesBox) {
      this.makesBox.addEventListener('click', function (event) {
        const button = event.target.closest('[data-lro-make]');
        if (!button) return;
        event.preventDefault();
        self.chooseMake(button.getAttribute('data-lro-make'));
      });
    }

    if (this.search) {
      this.search.addEventListener('input', function () {
        self.runSearch();
        self.open();
      });
      this.search.addEventListener('focus', function () {
        if (self.state.make) { self.runSearch(); self.open(); }
      });
      this.search.addEventListener('keydown', this.onSearchKey.bind(this));
      this.search.addEventListener('blur', function () {
        window.setTimeout(function () {
          if (!self.listbox.contains(document.activeElement)) self.close();
        }, 120);
      });
    }

    if (this.listbox) {
      /* mousedown, not click: it fires before the input's blur closes us. */
      this.listbox.addEventListener('mousedown', function (event) {
        const option = event.target.closest('[data-lro-option]');
        if (!option) return;
        event.preventDefault();
        self.chooseModelAt(parseInt(option.getAttribute('data-lro-option'), 10));
      });
      this.listbox.addEventListener('click', function (event) {
        const option = event.target.closest('[data-lro-option]');
        if (!option) return;
        event.preventDefault();
        self.chooseModelAt(parseInt(option.getAttribute('data-lro-option'), 10));
      });
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', function () {
        self.clearModel();
        if (self.search) self.search.focus();
      });
    }

    if (this.trail) {
      this.trail.addEventListener('click', function (event) {
        const button = event.target.closest('[data-lro-back]');
        if (!button) return;
        event.preventDefault();
        self.goBackTo(button.getAttribute('data-lro-back'));
      });
    }

    if (this.resetButton) {
      this.resetButton.addEventListener('click', function () { self.reset(); });
    }

    /* The panel's own button is a real link, so a plain click, a middle click
       and "copy link address" all behave. This only covers the keyboard case
       where the form is submitted instead. */
    this.form.addEventListener('submit', function (event) {
      const url = self.buildUrl();
      if (!url) return;                       /* let the search form do its job */
      event.preventDefault();
      window.location.assign(url);
    });

    window.addEventListener('hashchange', function () {
      const parsed = self.readHash();
      if (!parsed) return;
      self.pendingHash = parsed;
      if (self.index) self.applyHash();
      else self.warm();
    });
  }

  /* ---- state ---------------------------------------------------------- */

  findBrand(nameOrHandle) {
    if (!this.index) return null;
    const needle = String(nameOrHandle);
    const brands = this.index.brands;
    for (let i = 0; i < brands.length; i += 1) {
      if (brands[i].name === needle || brands[i].handle === needle) return brands[i];
    }
    return null;
  }

  chooseMake(name, options) {
    const self = this;
    const quiet = options && options.quiet;
    const promise = this.index ? Promise.resolve(this.index) : this.warm();

    return promise.then(function (index) {
      if (!index) return;
      const brand = self.findBrand(name);
      if (!brand) return;

      self.state.make = brand;
      self.state.model = null;
      self.state.part = null;
      decodeModels(brand, index);
      decodeBrandCats(brand, index);

      self.markMakes();
      self.setStep('make', 'done');
      self.setStep('model', 'active');
      self.setStep('part', (index.partial || brand.cats.length) ? 'ready' : 'locked');

      if (self.search) {
        self.search.disabled = false;
        self.search.value = '';
        self.search.placeholder = interpolate(
          self.strings.modelPlaceholderReady || 'Search {model_count} {brand} models',
          { model_count: formatCount(brand.models.length), brand: brand.name }
        );
      }
      self.renderModelHelp();

      self.runSearch();
      self.renderParts();
      self.renderTrail();
      self.syncForm();
      self.renderStatus();
      self.writeHash();

      if (!quiet && self.search) {
        self.search.focus();
        self.open();
      }
    });
  }

  chooseModelAt(position) {
    const model = this.hits[position];
    if (model) this.chooseModel(model);
  }

  chooseModel(model, options) {
    const quiet = options && options.quiet;
    this.state.model = model;
    this.state.part = null;

    if (this.search) this.search.value = model.name;
    if (this.clearButton) this.clearButton.hidden = false;
    this.close();

    this.setStep('model', 'done');
    this.setStep('part', 'active');
    this.renderParts();
    this.renderTrail();
    this.syncForm();
    this.renderStatus();
    this.writeHash();

    if (!quiet) {
      const first = this.partsBox && this.partsBox.querySelector('a, button');
      if (first) first.focus();
      else if (this.goButton && !this.goButton.hidden) this.goButton.focus();
    }
  }

  choosePart(cat) {
    this.state.part = cat;
    this.renderParts();
    this.renderTrail();
    this.syncForm();
    this.renderStatus();
  }

  clearModel() {
    this.state.model = null;
    this.state.part = null;
    if (this.search) this.search.value = '';
    if (this.clearButton) this.clearButton.hidden = true;
    this.setStep('model', 'active');
    this.setStep('part', this.state.make ? 'ready' : 'locked');
    this.runSearch();
    this.renderParts();
    this.renderTrail();
    this.syncForm();
    this.renderStatus();
    this.writeHash();
  }

  goBackTo(step) {
    if (step === 'make') this.reset();
    else if (step === 'model') this.clearModel();
  }

  reset() {
    this.state = { make: null, model: null, part: null };
    if (this.search) {
      this.search.value = '';
      this.search.disabled = true;
      this.search.placeholder = this.strings.modelPlaceholder || '';
    }
    if (this.clearButton) this.clearButton.hidden = true;
    this.renderModelHelp();
    this.close();
    this.markMakes();
    this.setStep('make', 'active');
    this.setStep('model', 'locked');
    this.setStep('part', 'locked');
    this.renderParts();
    this.renderTrail();
    this.syncForm();
    this.renderStatus();
    this.writeHash();
    const first = this.makesBox && this.makesBox.querySelector('[data-lro-make]');
    if (first) first.focus();
  }

  /* ---- the model listbox ---------------------------------------------- */

  runSearch() {
    if (!this.state.make || !this.listbox) return;
    const result = searchModels(
      this.state.make, this.search ? this.search.value : '', this.maxOptions
    );
    this.hits = result.hits;
    this.activeIndex = -1;

    if (!result.hits.length) {
      this.listbox.innerHTML = '';
      this.listbox.hidden = true;
      if (this.search) this.search.setAttribute('aria-expanded', 'false');
      this.renderModelHelp('emptyNoMatch');
      return;
    }

    const showCounts = this.showCounts;
    const parts = [];
    for (let i = 0; i < result.hits.length; i += 1) {
      const model = result.hits[i];
      parts.push(
        '<li class="lro-finder__option" role="option" aria-selected="false"' +
        ' id="' + this.uid + '-opt-' + i + '" data-lro-option="' + i + '">' +
        '<span class="lro-finder__option-name">' + highlight(model.name, result.query) + '</span>' +
        (showCounts && model.count
          ? '<span class="lro-finder__option-count lro-tabular">' + formatCount(model.count) + '</span>'
          : '') +
        '</li>'
      );
    }
    if (result.total > result.hits.length) {
      parts.push(
        '<li class="lro-finder__more" role="presentation">' +
        escapeHtml(interpolate(this.strings.moreModels || '{count} more. Keep typing.', {
          count: formatCount(result.total - result.hits.length)
        })) + '</li>'
      );
    }
    this.listbox.innerHTML = parts.join('');
    this.renderModelHelp();
  }

  /** The one line under the search field. It is a different sentence at every
   *  stage, and no sentence at all once the model is settled, because by then
   *  step three is doing the talking. */
  renderModelHelp(force) {
    if (!this.modelHelp) return;
    let key = force;
    if (!key) {
      if (!this.state.make) key = 'emptyNoBrand';
      else if (!this.state.model) key = 'emptyNoModel';
    }
    this.modelHelp.hidden = !key;
    this.modelHelp.textContent = key ? (this.strings[key] || '') : '';
  }

  open() {
    if (!this.listbox || !this.hits.length) return;
    this.listbox.hidden = false;
    if (this.search) this.search.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.listbox) return;
    this.listbox.hidden = true;
    this.activeIndex = -1;
    if (this.search) {
      this.search.setAttribute('aria-expanded', 'false');
      this.search.removeAttribute('aria-activedescendant');
    }
    const active = this.listbox.querySelector('.is-active');
    if (active) {
      active.classList.remove('is-active');
      active.setAttribute('aria-selected', 'false');
    }
  }

  setActive(next) {
    if (!this.hits.length) return;
    const options = this.listbox.querySelectorAll('[data-lro-option]');
    if (!options.length) return;
    if (this.activeIndex >= 0 && options[this.activeIndex]) {
      options[this.activeIndex].classList.remove('is-active');
      options[this.activeIndex].setAttribute('aria-selected', 'false');
    }
    let index = next;
    if (index < 0) index = options.length - 1;
    if (index >= options.length) index = 0;
    this.activeIndex = index;
    const option = options[index];
    option.classList.add('is-active');
    option.setAttribute('aria-selected', 'true');
    if (this.search) this.search.setAttribute('aria-activedescendant', option.id);
    if (typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  }

  onSearchKey(event) {
    const open = this.listbox && !this.listbox.hidden;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) { this.runSearch(); this.open(); this.setActive(0); }
        else this.setActive(this.activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) { this.runSearch(); this.open(); this.setActive(-1); }
        else this.setActive(this.activeIndex - 1);
        break;
      case 'Home':
        if (open) { event.preventDefault(); this.setActive(0); }
        break;
      case 'End':
        if (open) { event.preventDefault(); this.setActive(this.hits.length - 1); }
        break;
      case 'Enter':
        /* Enter takes the highlighted row, or the top match when the shopper
           has typed but not arrowed. Only an empty list falls through to the
           form, where it means "go". */
        if (open && this.hits.length) {
          event.preventDefault();
          this.chooseModelAt(this.activeIndex >= 0 ? this.activeIndex : 0);
        }
        break;
      case 'Escape':
        if (open) { event.preventDefault(); this.close(); }
        else if (this.search && this.search.value) { event.preventDefault(); this.clearModel(); }
        break;
      case 'Tab':
        this.close();
        break;
      default:
        break;
    }
  }

  /* ---- rendering ------------------------------------------------------ */

  setStep(name, state) {
    const node = this.steps[name];
    if (node) node.setAttribute('data-state', state);

    /* Mirror onto the always-visible progress rail. The rail is the only part
       of the flow a phone shows in full, so it has to track the real steps
       exactly rather than keep a second copy of the state. */
    if (this.root) {
      const pip = this.root.querySelector('[data-lro-progress-step="' + name + '"]');
      if (pip) pip.setAttribute('data-state', state);
    }
  }

  markMakes() {
    if (!this.makesBox) return;
    const chosen = this.state.make ? this.state.make.name : null;
    const buttons = this.makesBox.querySelectorAll('[data-lro-make]');
    for (let i = 0; i < buttons.length; i += 1) {
      const isChosen = buttons[i].getAttribute('data-lro-make') === chosen;
      buttons[i].setAttribute('aria-pressed', isChosen ? 'true' : 'false');
      buttons[i].classList.toggle('is-chosen', isChosen);
    }
  }

  /** Step three. A model's own categories when we have them, the make's
   *  otherwise, so the shopper can skip the model entirely. */
  renderParts() {
    if (!this.partsBox) return;
    const make = this.state.make;
    if (!make) {
      this.partsBox.innerHTML = '';
      if (this.partHelp) {
        this.partHelp.hidden = false;
        this.partHelp.textContent = this.strings.emptyNoBrand || '';
      }
      return;
    }

    let list;
    if (this.state.model && this.state.model.cats) list = this.state.model.cats;
    else if (this.state.model && this.index.partial) {
      list = this.index.cats.map(function (cat) { return { cat: cat, count: 0 }; });
    } else list = make.cats || [];

    const chosenHandle = this.state.part ? this.state.part.handle : null;
    const pieces = [];

    const everything = this.state.model
      ? this.strings.allParts || 'All parts'
      : interpolate(this.strings.allBrandParts || 'All {brand} parts', { brand: make.name });
    const everythingCount = this.state.model ? this.state.model.count : make.count;

    pieces.push(this.partTile({
      href: this.urlFor(null),
      label: everything,
      count: everythingCount,
      chosen: !chosenHandle,
      wide: true,
      handle: ''
    }));

    for (let i = 0; i < list.length; i += 1) {
      const entry = list[i];
      pieces.push(this.partTile({
        href: this.urlFor(entry.cat),
        label: entry.cat.name,
        count: entry.count,
        chosen: entry.cat.handle === chosenHandle,
        wide: false,
        handle: entry.cat.handle
      }));
    }

    this.partsBox.innerHTML = pieces.join('');
    if (this.partHelp) {
      /* Once a model is chosen the tiles say everything; before that, explain
         what choosing one would do. Never repeat step two's own hint here. */
      this.partHelp.hidden = Boolean(this.state.model);
      this.partHelp.textContent = this.state.model ? '' : (this.strings.step3Hint || '');
    }
  }

  partTile(spec) {
    return '<a class="lro-finder__tile lro-finder__tile--part' +
      (spec.wide ? ' lro-finder__tile--all' : '') +
      (spec.chosen ? ' is-chosen' : '') +
      '" href="' + escapeHtml(spec.href) + '" data-lro-part="' + escapeHtml(spec.handle) + '">' +
      '<span class="lro-finder__tile-name">' + escapeHtml(spec.label) + '</span>' +
      (this.showCounts && spec.count
        ? '<span class="lro-finder__tile-count lro-tabular">' + formatCount(spec.count) + '</span>'
        : '') +
      '</a>';
  }

  renderTrail() {
    if (!this.trail) return;
    const pieces = [];
    if (this.state.make) {
      pieces.push(this.crumb('make', this.state.make.name, this.strings.changeMake));
    }
    if (this.state.model) {
      pieces.push(this.crumb('model', this.state.model.name, this.strings.changeModel));
    }
    this.trail.innerHTML = pieces.join('');
    this.trail.hidden = !pieces.length;
    if (this.resetButton) this.resetButton.hidden = !this.state.make;
  }

  crumb(step, label, action) {
    return '<li class="lro-finder__crumb">' +
      '<button type="button" class="lro-pill lro-finder__crumb-button" data-lro-back="' + step + '">' +
      '<span class="lro-finder__crumb-label">' + escapeHtml(label) + '</span>' +
      '<span class="lro-visually-hidden"> &mdash; ' + escapeHtml(action || 'change') + '</span>' +
      '<span class="lro-finder__crumb-x" aria-hidden="true">&times;</span>' +
      '</button></li>';
  }

  /** One visible sentence that is also the live region, so what a sighted
   *  shopper reads and what a screen reader announces are the same words. */
  renderStatus() {
    const url = this.buildUrl();
    if (this.goButton) {
      this.goButton.hidden = !url;
      if (url) {
        this.goButton.setAttribute('href', url);
        this.goButton.textContent = this.strings.submit || 'Show parts';
      }
    }
    if (!this.status) return;

    if (!this.state.make) { this.say(''); return; }
    if (!this.state.model) {
      const models = this.state.make.models ? this.state.make.models.length : 0;
      this.say(interpolate(this.strings.statusMake || '{model_count} {brand} models', {
        model_count: formatCount(models),
        brand: this.state.make.name
      }));
      return;
    }

    const count = this.state.part
      ? this.countFor(this.state.part)
      : this.state.model.count;
    if (!count) { this.say(this.strings.emptyNoResults || ''); return; }
    const template = count === 1
      ? (this.strings.resultsSingle || '1 part for {model}')
      : (this.strings.resultsHeading || '{count} parts for {model}');
    this.say(interpolate(template, {
      count: formatCount(count),
      model: this.state.model.name
    }));
  }

  countFor(cat) {
    const list = (this.state.model && this.state.model.cats) || [];
    for (let i = 0; i < list.length; i += 1) {
      if (list[i].cat.handle === cat.handle) return list[i].count;
    }
    return 0;
  }

  say(message) {
    if (!this.status) return;
    if (this.status.textContent === message) return;
    this.status.textContent = message;
  }

  /* ---- URLs ----------------------------------------------------------- */

  /** /collections/<make>/<model>+<part> - Shopify's native tag filtering. */
  urlFor(cat) {
    const make = this.state.make;
    if (!make) return this.collectionsUrl + '/all';
    let url = this.collectionsUrl + '/' + make.handle;
    const tags = [];
    if (this.state.model) tags.push(this.state.model.handle);
    if (cat) tags.push(cat.handle);
    if (tags.length) url += '/' + tags.join('+');
    return url;
  }

  buildUrl() {
    if (!this.state.make) return null;
    return this.urlFor(this.state.part);
  }

  /** Mirror the panel into the fallback form, so submitting it still works. */
  syncForm() {
    if (this.basicMake) this.basicMake.value = this.state.make ? this.state.make.name : '';
    if (this.basicPart) this.basicPart.value = this.state.part ? this.state.part.name : '';
    if (!this.basicModel) return;

    if (!this.state.model) {
      this.basicModel.value = this.state.make ? this.state.make.name : '';
      return;
    }
    const value = this.state.model.name;
    const options = this.basicModel.options;
    let found = false;
    for (let i = 0; i < options.length; i += 1) {
      if (options[i].value === value) { found = true; break; }
    }
    if (!found) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      option.setAttribute('data-lro-injected', 'true');
      this.basicModel.appendChild(option);
    }
    this.basicModel.value = value;
  }

  /* ---- deep links ------------------------------------------------------ */

  /** #lro-finder=apple-parts/iphone-13-mini/screens */
  readHash() {
    const hash = window.location.hash.replace(/^#/, '');
    const prefix = this.anchor + '=';
    if (hash.indexOf(prefix) !== 0) return null;
    const segments = decodeURIComponent(hash.slice(prefix.length)).split('/').filter(Boolean);
    if (!segments.length) return null;
    return { make: segments[0], model: segments[1] || null, part: segments[2] || null };
  }

  writeHash() {
    if (!window.history || !window.history.replaceState) return;
    let value = '';
    if (this.state.make) {
      value = this.state.make.handle;
      if (this.state.model) value += '/' + this.state.model.handle;
      if (this.state.part) value += '/' + this.state.part.handle;
    }
    const next = value
      ? '#' + this.anchor + '=' + value
      : window.location.pathname + window.location.search;
    try {
      window.history.replaceState(null, '', next);
    } catch (error) {
      /* file:// and some embedded browsers refuse. Not worth breaking over. */
    }
  }

  applyHash() {
    const wanted = this.pendingHash;
    if (!wanted || !this.index) return;
    this.pendingHash = null;
    /* Somebody who started tapping while the index was still on the wire has
       said more recently than the URL did what they want. */
    if (this.state.make) return;

    const self = this;
    this.chooseMake(wanted.make, { quiet: true }).then(function () {
      if (!self.state.make) return;
      if (wanted.model) {
        const models = self.state.make.models || [];
        for (let i = 0; i < models.length; i += 1) {
          if (models[i].handle === wanted.model) {
            self.chooseModel(models[i], { quiet: true });
            break;
          }
        }
      }
      if (wanted.part && self.index) {
        const cats = self.index.cats;
        for (let i = 0; i < cats.length; i += 1) {
          if (cats[i].handle === wanted.part) { self.choosePart(cats[i]); break; }
        }
      }
      if (self.root.scrollIntoView) {
        self.root.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    });
  }
}

/* --------------------------------------------------------------------------
   Boot
   -------------------------------------------------------------------------- */

const started = new WeakSet();

function start(scope) {
  const roots = (scope || document).querySelectorAll('[data-lro-finder]');
  for (let i = 0; i < roots.length; i += 1) {
    if (started.has(roots[i])) continue;
    started.add(roots[i]);
    try {
      new DeviceFinder(roots[i]);
    } catch (error) {
      /* Leave the fallback form exactly as it was rendered. */
      roots[i].classList.remove('lro-finder--enhanced');
      if (window.console && window.console.warn) {
        window.console.warn('[lro-finder]', error);
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () { start(document); });
} else {
  start(document);
}

/* Theme editor: sections are swapped in and out of the DOM as they are edited. */
document.addEventListener('shopify:section:load', function (event) {
  start(event.target);
});
